import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Feedback, useResource } from "@/components/workflow/shared";
import { equipmentE2Api, type Warning } from "@/lib/api/oprema-e2";
import { useEquipmentAccess } from "./shared";

export function EquipmentTasks({
  placeId,
  warnings,
}: {
  placeId?: string;
  warnings?: Warning[];
}) {
  const access = useEquipmentAccess();
  const navigate = useNavigate();
  const resource = useResource(
    `equipment-tasks:${placeId}:${access.read}`,
    useCallback(
      async (signal) => {
        if (!access.read) return null;
        const [notices, requests] = await Promise.all([
          warnings
            ? Promise.resolve(warnings)
            : equipmentE2Api.warnings(placeId, signal),
          equipmentE2Api.requests(placeId, signal),
        ]);
        return {
          notices,
          requests: requests.filter((row) => row.status === "otvoren"),
        };
      },
      [placeId, access.read, warnings],
    ),
  );
  const notices = resource.data?.notices ?? [];
  const requests = resource.data?.requests ?? [];
  return (
    <section className="equipment-field" aria-label="Treba odraditi u Opremi">
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Ponovi provjeru zadataka
        </Button>
      )}
      {(notices.length > 0 || requests.length > 0) && (
        <h2 className="equipment-label">Treba odraditi</h2>
      )}
      {notices.map((notice, index) => (
        <div
          key={`${notice.vrsta}:${notice.mjesto_id}:${notice.najam_id ?? index}`}
          className={`equipment-todo ${
            notice.boja === "neutral"
              ? "equipment-neutral"
              : notice.boja === "bad"
                ? "equipment-danger"
                : "equipment-warning"
          }`}
        >
          <b>{notice.poruka}</b>
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                notice.najam_id
                  ? `/oprema/najam?najam=${encodeURIComponent(notice.najam_id)}`
                  : `/oprema/mjesta/${notice.mjesto_id}/inventura`,
              )
            }
          >
            {notice.najam_id ? "Otvori najam" : "Otvori inventuru"}
          </Button>
        </div>
      ))}
      {requests.map((request) => (
        <div className="equipment-todo equipment-info" key={request.id}>
          <div>
            <b>Zahtjev iz Baze čeka slanje</b>
            <p>
              {request.stavke
                .map((line) => `${line.kolicina} ${line.naziv}`)
                .join(" · ")}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `/oprema/mjesta/${request.mjesto_id}/zahtjevi?zahtjev=${encodeURIComponent(request.id)}`,
              )
            }
          >
            Otvori zahtjev
          </Button>
        </div>
      ))}
    </section>
  );
}
